import type { LoginResponse } from './types';

/**
 * Token-shape tolerance for /v1/login, /v1/refresh, /v1/code. The user
 * service has shipped two shapes at various points:
 * flat: { accessToken, refreshToken, expiresIn,...userFields }
 * nested: { tokenResponse: { accessToken, refreshToken, expiresIn },...userFields }
 * handled this defensively in its reducer:
 * `data.payload.tokenResponse?.accessToken || data.payload.accessToken`
 * Without this, a backend revert to the nested shape would 500-out the
 * client login flow with a confusing "missing tokens" error.
 * This helper normalizes either shape to the flat one. The variant lives
 * in ONE place so the three call sites (login, exchangeCode, refresh)
 * stay simple.
 */

interface NestedTokenResponse {
  tokenResponse?: {
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    tokenType?: string;
    scope?: string;
  };
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  tokenType?: string;
  scope?: string;
  isError?: boolean;
}

/**
 * Returns the resolved `{accessToken, refreshToken, expiresIn,...}` slice
 * of the response, or null if the response doesn't carry tokens (either
 * shape is missing the required() fields).
 */
export function extractTokens(
  payload: NestedTokenResponse | undefined | null,
): {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType?: string;
  scope?: string;
} | null {
  if (!payload || payload.isError) return null;
  const nested = payload.tokenResponse;
  const accessToken = nested?.accessToken ?? payload.accessToken;
  const refreshToken = nested?.refreshToken ?? payload.refreshToken;
  const expiresIn = nested?.expiresIn ?? payload.expiresIn;
  const tokenType = nested?.tokenType ?? payload.tokenType;
  const scope = nested?.scope ?? payload.scope;
  if (!accessToken || !refreshToken || typeof expiresIn !== 'number') return null;
  return {
    accessToken,
    refreshToken,
    expiresIn,
...(tokenType !== undefined && { tokenType }),
...(scope !== undefined && { scope }),
  };
}

/**
 * Normalize a LoginResponse payload that may carry tokens nested under
 * `tokenResponse`. Returns the same payload with the tokens hoisted to the
 * top level, so the rest of the auth code can treat it as flat.
 * Returns null if no tokens are extractable — caller should treat that as
 * an auth failure.
 */
export function normalizeLoginResponse(
  payload: LoginResponse | (NestedTokenResponse & Omit<LoginResponse, 'accessToken' | 'refreshToken' | 'expiresIn'>),
): LoginResponse | null {
  const tokens = extractTokens(payload as NestedTokenResponse);
  if (!tokens) return null;
  // Keep every user-shaped field from the original payload, then overlay
  // the flat tokens. Strip the `tokenResponse` envelope.
  const { tokenResponse: _strip,...rest } = payload as unknown as NestedTokenResponse &
    Record<string, unknown>;
  return {
...(rest as unknown as LoginResponse),
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresIn: tokens.expiresIn,
...(tokens.tokenType !== undefined && { tokenType: tokens.tokenType }),
...(tokens.scope !== undefined && { scope: tokens.scope }),
  };
}
